import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentLinkService } from '../../../services/payment-link.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';
import { ConfirmationConfig } from '../../shared/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'app-view-payment-link',
    templateUrl: './view-payment-link.component.html',
    styleUrls: ['./view-payment-link.component.scss']
})
export class ViewPaymentLinkComponent implements OnInit {
    paymentLink: any = null;
    paymentLinkId: number = 0;

    // Modal de confirmación
    showConfirmationModal = false;
    confirmationConfig: ConfirmationConfig = {
        title: '¿Desactivar link de pago?',
        message: 'Esta acción desactivará el link de pago y no podrá ser utilizado para realizar pagos.',
        confirmText: 'Sí, desactivar',
        cancelText: 'Cancelar',
        type: 'danger',
        icon: 'fa-ban',
        showIcon: true
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private paymentLinkService: PaymentLinkService,
        private loadingService: LoadingService,
        private toastService: ToastService
    ) { }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.paymentLinkId = +params['id'];
            this.loadPaymentLink();
        });
    }

    loadPaymentLink(): void {
        this.loadingService.show();

        this.paymentLinkService.getPaymentLinkById(this.paymentLinkId).subscribe({
            next: (response) => {
                this.loadingService.hide();
                if (response.isSuccess) {
                    this.paymentLink = response.data;
                } else {
                    this.toastService.showToast('error', 'Error al cargar el link de pago');
                    this.router.navigate(['/dashboard/payment-links']);
                }
            },
            error: (error) => {
                this.loadingService.hide();
                console.error('Error al cargar payment link:', error);
                this.toastService.showToast('error', 'Error al cargar el link de pago');
                this.router.navigate(['/dashboard/payment-links']);
            }
        });
    }

    goBack(): void {
        this.router.navigate(['/dashboard/payment-links']);
    }

    openPaymentLink(): void {
        if (this.paymentLink?.paymentUrl) {
            window.open(this.paymentLink.paymentUrl, '_blank');
        }
    }

    async copyToClipboard(): Promise<void> {
        if (!this.paymentLink?.paymentUrl) return;

        try {
            await this.paymentLinkService.copyToClipboard(this.paymentLink.paymentUrl);
            this.toastService.showToast('success', '✅ Link copiado al portapapeles');
        } catch (error) {
            this.toastService.showToast('error', 'Error al copiar el link');
        }
    }

    deactivatePaymentLink(): void {
        this.showConfirmationModal = true;
    }

    async onConfirmDeactivation(): Promise<void> {
        this.showConfirmationModal = false;
        try {
            this.loadingService.show();
            await this.paymentLinkService.deactivatePaymentLink(this.paymentLinkId).toPromise();
            this.toastService.showToast('success', '✅ Link de pago desactivado exitosamente');
            this.loadPaymentLink();
        } catch (error: any) {
            console.error('Error al desactivar payment link:', error);
            const errorMessage = error?.error?.errorMessage || 'Error al desactivar el link de pago';
            this.toastService.showToast('error', errorMessage);
        } finally {
            this.loadingService.hide();
        }
    }

    onCancelDeactivation(): void {
        this.showConfirmationModal = false;
    }

    formatCurrency(amount: number, currencyCode: string = 'COP'): string {
        if (amount === null || amount === undefined) return '-';
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: currencyCode === 'COP' ? 0 : 2,
            maximumFractionDigits: currencyCode === 'COP' ? 0 : 2
        }).format(amount);
    }

    formatDate(date: string): string {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('es-CO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getStatusClass(): string {
        if (!this.paymentLink?.isActive) return 'bg-gray-100 text-gray-600';
        if (this.paymentLink?.isExpired) return 'bg-red-100 text-red-600';
        return 'bg-green-100 text-green-600';
    }

    getStatusText(): string {
        if (!this.paymentLink?.isActive) return 'Inactivo';
        if (this.paymentLink?.isExpired) return 'Expirado';
        return 'Activo';
    }
}
